import { Box, Stack } from "@mui/joy";
import DarkTypography from "components/common/DarkTypography";
import { excursionTooltipParamLabelColor, excursionTooltipSublineColor } from "styles/globalStyles";
import ExcursionTooltipGroupsBody, {
  EXCURSION_GROUPS_SURFACE_DRAWER,
} from "../../../shared/excursionModal/display/ExcursionTooltipGroupsBody";
import ExcursionTooltipParamEntry from "../../../shared/excursionModal/display/ExcursionTooltipParamEntry";
import { excursionTooltipExcursionParamEntries } from "../../../shared/excursionModal/display/excursionTooltipHelpers";
import type {
  BookingStatus,
  Excursion,
  ExcursionChipDropPending,
  ExcursionTemplateLimitsItem,
  GroupLabels,
} from "api/excursions/Excursion.typed";
import { ExcursionStatusCancelled, ExcursionStatusPending } from "api/excursions/Excursion.typed";

export type ExcursionPlanningSortieDrawerContentProps = {
  excursion: Excursion;
  canEdit: boolean;
  groupLabels?: GroupLabels[];
  makeGroupChipDropHandlers?: (targetExcursionId: number, targetGroupIndex: number) => object;
  excursionTemplatesList?: ExcursionTemplateLimitsItem[];
  bookingStatusMap?: Record<number, BookingStatus>;
  getCustomerInProgressTrainingsTooltip?: (customerId: number) => string | null;
  onClientDragStart?: (sourceExcursionId: number) => void;
  onClientDragEnd?: () => void;
  chipDropPending?: ExcursionChipDropPending | null;
};

export default function ExcursionPlanningSortieDrawerContent({
  excursion,
  canEdit,
  groupLabels,
  makeGroupChipDropHandlers,
  excursionTemplatesList,
  bookingStatusMap,
  getCustomerInProgressTrainingsTooltip,
  onClientDragStart,
  onClientDragEnd,
  chipDropPending = null,
}: ExcursionPlanningSortieDrawerContentProps) {
  const cancellationMotif =
    excursion.status === ExcursionStatusCancelled && excursion.cancellation_reason?.trim()
      ? excursion.cancellation_reason.trim()
      : "";

  const groups = excursion.groups ?? [];
  const canEditGroups =
    canEdit && (excursion.status ?? ExcursionStatusPending) === ExcursionStatusPending;

  const entries = excursionTooltipExcursionParamEntries(excursion, excursionTemplatesList);

  return (
    <Stack spacing={2} sx={{ minWidth: 0 }}>
      {(cancellationMotif !== "" || entries.length > 0) && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, minWidth: 0 }}>
          {cancellationMotif !== "" ? (
            <DarkTypography
              level="body-xs"
              sx={{
                color: excursionTooltipSublineColor,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              {cancellationMotif}
            </DarkTypography>
          ) : null}

          {entries.length > 0 ? (
            <Stack spacing={0.35} sx={{ minWidth: 0 }}>
              {entries.map(([key, val]) => (
                <ExcursionTooltipParamEntry
                  key={key}
                  label={key}
                  value={val}
                  color={excursionTooltipParamLabelColor}
                />
              ))}
            </Stack>
          ) : null}
        </Box>
      )}

      {/* Drawer: drag source only; client/user drops target planning tooltips / cards (Alt/⌥ = copy). */}
      <ExcursionTooltipGroupsBody
        excursion={excursion}
        groups={groups}
        groupLabels={groupLabels}
        groupDropHandlers={canEditGroups ? makeGroupChipDropHandlers : undefined}
        canEdit={canEditGroups}
        bookingStatusMap={bookingStatusMap}
        onClientDragStart={onClientDragStart}
        onClientDragEnd={onClientDragEnd}
        getCustomerInProgressTrainingsTooltip={getCustomerInProgressTrainingsTooltip}
        reconnectDropSurfacePointerEventsInsideFloater={false}
        groupSurface={EXCURSION_GROUPS_SURFACE_DRAWER}
        chipDropPending={chipDropPending}
      />
    </Stack>
  );
}
